import {
  Box,
  Card,
  Divider,
  Stack,
  Text,
  UnstyledButton,
  createStyles,
  getStylesRef,
  useMantineTheme,
} from '@mantine/core'
import React, { memo } from 'react'
import { transitionStyles } from 'src/styles'
import { BrandNpm, BrandPython, BrandVscode, ExternalLink } from 'tabler-icons-react'
import Zoom from 'react-reveal/Zoom'

export interface OpensourceCardProps {
  name: string
  description: string
  code: string
  category: string
  link: string
}

const useStyles = createStyles(theme => ({
  card: {
    height: 220,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colorScheme === 'dark' ? '#303030' : theme.colors.gray[1],
    border: theme.colorScheme === 'dark' ? '1px solid rgba(229, 231, 235, 0.2)' : '1px solid rgba(107, 111, 115, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    transition: transitionStyles.themeChange,
    boxShadow: theme.colorScheme === 'dark' ? 'none' : theme.shadows.lg,

    // scale on hover
    '&:hover': {
      transform: 'scale(1.07)',
      transition: transitionStyles.themeChange,
    },

    [`&:hover .${getStylesRef('linkButton')}`]: {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.blue[8] : theme.colors.blue[2],
      transition: transitionStyles.themeChange,
      border: 'none',
    },

    [`@media (max-width: ${theme.breakpoints.md})`]: {
      width: 350,
    },
  },
  imageStyle: {
    img: {
      maxHeight: '70px',
    },
  },
  iconWrapper: {
    height: 60,
    width: 60,
    marginTop: -40,
    borderRadius: '50%',
    backgroundColor: 'grey',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  dividerStyle: {
    color: theme.colorScheme === 'dark' ? 'rgba(229, 231, 235, 1)' : 'rgba(107, 111, 115, 0.3)',
    margin: theme.spacing.xs,
  },
  title: {
    color: theme.colorScheme === 'dark' ? theme.colors.gray[2] : theme.colors.gray[8],
    fontSize: 18,
    fontWeight: 600,
  },
  subTitle: {
    color: theme.colorScheme === 'dark' ? theme.colors.blue[4] : theme.colors.blue[6],
    fontSize: 16,
    fontWeight: 600,
    textTransform: 'capitalize',
    marginBottom: 8,
  },
  description: {
    color: theme.colorScheme === 'dark' ? theme.colors.gray[3] : theme.colors.gray[7],
    fontSize: 14,
    marginBottom: 8,
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkButton: {
    ref: getStylesRef('linkButton'),
    color: theme.colorScheme === 'dark' ? theme.colors.gray[4] : theme.colors.gray[7],
    borderRadius: theme.radius.lg,
    marginBlock: 5,
    fontSize: 14,
    padding: 5,
    width: 180,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.blue[8] : theme.colors.blue[2],
      transition: transitionStyles.themeChange,
      border: 'none',
    },
  },
  linkText: {
    color: 'inherit',
    textDecoration: 'inherit',
    marginRight: 4,
  },
}))

export const OpensourceCard: React.FC<OpensourceCardProps> = memo(props => {
  const { name, description, category, link, code } = props
  const theme = useMantineTheme()

  const ICON_SIZE = 36

  const dividerColor = theme.colorScheme === 'dark' ? 'rgba(229, 231, 235, 0.2)' : 'rgba(107, 111, 115, 0.3)'
  const iconColor = theme.colorScheme === 'dark' ? theme.colors.gray[4] : theme.colors.gray[7]

  const getColorFromCode = (code: string) => {
    switch (code) {
      case 'VSCODE':
        return '#0078d7'
      case 'PYTHON':
        return '#ffde57'
      case 'NPM':
        return '#cb3837'
    }
  }

  const getIconFromCode = (code: string) => {
    switch (code) {
      case 'VSCODE':
        return <BrandVscode size={ICON_SIZE} color="white" />
      case 'PYTHON':
        return <BrandPython size={ICON_SIZE} color="gray" />
      case 'NPM':
        return <BrandNpm size={ICON_SIZE} color="white" />
    }
  }

  const { classes } = useStyles()

  return (
    <Stack align="center">
      <Zoom>
        <Card className={classes.card}>
          <Text className={classes.title}>{category}</Text>
          <Divider color={dividerColor} className={classes.dividerStyle} />
          <Text className={classes.subTitle}>{name ?? ''}</Text>
          <Text className={classes.description}>{description ?? ''}</Text>
          <Box className={classes.buttonWrapper}>
            <UnstyledButton className={classes.linkButton}>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'inherit',
                  textDecoration: 'inherit',
                  marginRight: 4,
                }}
              >
                View {code.toLowerCase().includes('vscode') ? 'Extension' : 'Package'}
              </a>
              <ExternalLink size={18} color={iconColor} />
            </UnstyledButton>
          </Box>
        </Card>
        <Box className={classes.iconWrapper} sx={{ backgroundColor: getColorFromCode(code) }}>
          {getIconFromCode(code)}
        </Box>
      </Zoom>
    </Stack>
  )
})
